import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TrendingUp,
  ArrowLeft,
  Clock,
  ChevronRight,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "@/App";

const History = () => {
  const { user } = useContext(AuthContext);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSignal, setSelectedSignal] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/signals/history`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSignals(response.data);
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, instrument) => {
    if (!price) return "—";
    return price.toFixed(instrument === "USDJPY" ? 3 : 5);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            <span className="font-bold">Aurex AI</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-black">TRADE HISTORY</h1>
          <p className="text-muted-foreground mt-2">
            View all your generated signals and their details
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Signal List */}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Loading history...</p>
                </div>
              ) : signals.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Time</TableHead>
                      <TableHead className="text-muted-foreground">Instrument</TableHead>
                      <TableHead className="text-muted-foreground">Confidence</TableHead>
                      <TableHead className="text-muted-foreground">Direction</TableHead>
                      <TableHead className="text-muted-foreground">Entry</TableHead>
                      <TableHead className="text-muted-foreground w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signals.map((signal) => (
                      <TableRow
                        key={signal.signal_id}
                        className={`border-border cursor-pointer transition-colors ${
                          selectedSignal?.signal_id === signal.signal_id
                            ? "bg-secondary"
                            : "hover:bg-secondary/50"
                        }`}
                        onClick={() => setSelectedSignal(signal)}
                        data-testid={`signal-row-${signal.signal_id}`}
                      >
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            {formatDate(signal.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={signal.instrument.includes("XA") ? "text-amber-500 font-bold" : "font-bold"}>
                            {signal.instrument}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs font-bold uppercase ${
                            signal.signal_type === "TRADE"
                              ? "bg-green-500/20 text-green-400"
                              : signal.signal_type === "CAUTION_TRADE"
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-red-500/20 text-red-400"
                          }`}>
                            {signal.confidence || 0}%
                          </span>
                        </TableCell>
                        <TableCell>
                          {signal.direction ? (
                            <span className={`font-bold ${
                              signal.direction === "BUY" ? "text-green-500" : "text-red-500"
                            }`}>
                              {signal.direction}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">
                          {signal.entry ? formatPrice(signal.entry, signal.instrument) : "—"}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 mx-auto bg-secondary flex items-center justify-center mb-4">
                    <Clock className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">No signals yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Generate your first signal from the dashboard
                  </p>
                  <Link to="/dashboard">
                    <Button className="bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Signal Detail */}
          <div className="lg:col-span-5">
            {selectedSignal ? (
              <div className={`bg-card border ${
                selectedSignal.signal_type === "TRADE"
                  ? selectedSignal.direction === "BUY" ? "border-green-500/50" : "border-red-500/50"
                  : "border-amber-500/50"
              } p-6 sticky top-24 animate-slide-up`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {selectedSignal.signal_type === "TRADE" ? (
                      <span className={`px-3 py-1.5 font-bold uppercase text-sm ${
                        selectedSignal.direction === "BUY"
                          ? "bg-green-600 text-white"
                          : "bg-red-600 text-white"
                      }`}>
                        {selectedSignal.direction}
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 bg-amber-600 text-white font-bold uppercase text-sm">
                        No Trade
                      </span>
                    )}
                    <span className={`text-xl font-bold ${
                      selectedSignal.instrument.includes("XA") ? "text-amber-500" : ""
                    }`}>
                      {selectedSignal.instrument}
                    </span>
                  </div>
                  <span className="font-mono text-sm text-muted-foreground">
                    {selectedSignal.bias_tf}
                  </span>
                </div>

                {selectedSignal.signal_type === "TRADE" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="p-3 bg-secondary/50 border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Entry</div>
                        <div className="font-mono font-bold" data-price>
                          {formatPrice(selectedSignal.entry, selectedSignal.instrument)}
                        </div>
                      </div>
                      <div className="p-3 bg-red-500/10 border border-red-500/30">
                        <div className="text-xs text-red-400 mb-1">Stop Loss</div>
                        <div className="font-mono font-bold text-red-400" data-price>
                          {formatPrice(selectedSignal.stop_loss, selectedSignal.instrument)}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-6">
                      <div className="p-3 bg-green-500/10 border border-green-500/30">
                        <div className="text-xs text-green-400 mb-1">TP1</div>
                        <div className="font-mono text-sm font-bold text-green-400" data-price>
                          {formatPrice(selectedSignal.tp1, selectedSignal.instrument)}
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 border border-green-500/30">
                        <div className="text-xs text-green-400 mb-1">TP2</div>
                        <div className="font-mono text-sm font-bold text-green-400" data-price>
                          {formatPrice(selectedSignal.tp2, selectedSignal.instrument)}
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 border border-green-500/30">
                        <div className="text-xs text-green-400 mb-1">TP3</div>
                        <div className="font-mono text-sm font-bold text-green-400" data-price>
                          {formatPrice(selectedSignal.tp3, selectedSignal.instrument)}
                        </div>
                      </div>
                    </div>

                    {selectedSignal.why && (
                      <div className="mb-4">
                        <div className="text-xs text-muted-foreground mb-2">WHY</div>
                        <ul className="space-y-1">
                          {selectedSignal.why.map((reason, index) => (
                            <li key={index} className="text-sm flex items-start gap-2">
                              <span className="text-green-500">•</span>
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {selectedSignal.blockers && (
                      <div className="mb-4">
                        <div className="text-xs text-muted-foreground mb-2">BLOCKERS</div>
                        <ul className="space-y-2">
                          {selectedSignal.blockers.map((blocker, index) => (
                            <li key={index} className="text-sm p-2 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                              {blocker}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedSignal.what_would_change && (
                      <div className="p-3 bg-green-500/10 border border-green-500/30">
                        <div className="text-xs text-green-400 mb-1">What Would Change It</div>
                        <p className="text-sm">{selectedSignal.what_would_change}</p>
                      </div>
                    )}
                  </>
                )}

                {selectedSignal.explanation && (
                  <div className="mt-4 p-3 bg-secondary/30 border border-border">
                    <div className="text-xs text-muted-foreground mb-1">AI Analysis</div>
                    <p className="text-sm">{selectedSignal.explanation}</p>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    Generated: {formatDate(selectedSignal.created_at)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border p-6 text-center sticky top-24">
                <div className="w-16 h-16 mx-auto bg-secondary flex items-center justify-center mb-4">
                  <ChevronRight className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-bold mb-2">Select a Signal</h3>
                <p className="text-muted-foreground text-sm">
                  Click on a signal from the list to view its details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default History;
